import React, { useState, useCallback, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, LayoutList } from 'lucide-react'
import { detectOS } from '../../../../lib/platform/detection'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../../../../components/ui/dropdown-menu'
import type { AddTaskProps } from './AddTask.types'
import './AddTask.scss'

/**
 * AddTask — "add task" affordance below the project title.
 *
 * Three CSS-driven hover states:
 * - **Near** (default): subtle plus-circle outline + arrow, low opacity
 * - **Over**: mouse anywhere on the row — shortcut badge visible, plus filled in space color
 * - **Over Big**: mouse directly over the trigger area — plus circle scales up
 *
 * Click anywhere on the row → triggers "New Task" action directly.
 * Click on the plus icon → opens a dropdown with "New Task" and "New Section".
 * The dropdown opens on mouse release (not on press) so the pressed animation plays first.
 *
 * @example
 * <AddTask onAddTask={() => {}} onAddSection={() => {}} />
 */
export const AddTask: React.FC<AddTaskProps> = ({ className, onAddTask, onAddSection, shortcutKey = 'A', sectionOnly, taskOnly, disabled }) => {
  const { t } = useTranslation()
  const sectionShortcut = useMemo(() => detectOS() === 'mac' ? '⌘⇧N' : 'Ctrl+Shift+N', [])
  const [open, setOpen] = useState(false)
  const closedAtRef = useRef(0)

  // Prevent Radix default (opens on pointerdown) — we open on pointerup instead.
  // preventDefault on pointerdown suppresses mouse compat events, so we use pointerup.
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault()
  }, [])

  // Track when Radix closes the dropdown (dismiss, item select, etc.)
  const handleOpenChange = useCallback((next: boolean) => {
    if (!next) closedAtRef.current = Date.now()
    setOpen(next)
  }, [])

  // Row click → default action directly (no dropdown).
  // sectionOnly: adds section. Normal: adds task.
  // Skip when the dropdown is open or just closed (item selection handles its own action).
  const handleRowPointerUp = useCallback(() => {
    if (disabled || open || Date.now() - closedAtRef.current < 300) return
    if (sectionOnly) { onAddSection?.() } else { onAddTask?.() }
  }, [onAddTask, onAddSection, sectionOnly, open, disabled])

  // Plus button click → toggle dropdown.
  // Skip if Radix just closed it (dismiss fires on pointerdown before our pointerup).
  const handlePlusPointerUp = useCallback((e: React.PointerEvent) => {
    e.stopPropagation()
    if (Date.now() - closedAtRef.current < 300) return
    setOpen(prev => !prev)
  }, [])

  return (
    <div
      className={`add-task${open ? ' add-task--open' : ''}${className ? ` ${className}` : ''}`}
      onPointerUp={handleRowPointerUp}
    >
      <div className="add-task__controls">
        {/* Keyboard shortcut badge */}
        <div className="add-task__shortcut">
          <span className="add-task__key">{shortcutKey}</span>
        </div>

        {/* Plus circle + arrow — hover target for "Over Big" */}
        <div className="add-task__trigger">
          {(sectionOnly || taskOnly) ? (
            <button
              className="add-task__plus"
              type="button"
              tabIndex={-1}
              onPointerUp={(e) => { e.stopPropagation(); if (disabled) return; sectionOnly ? onAddSection?.() : onAddTask?.() }}
            >
              <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 1v12M1 7h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          ) : (
            <>
              <DropdownMenu open={open} onOpenChange={handleOpenChange}>
                <DropdownMenuTrigger asChild>
                  <button
                    className="add-task__plus"
                    type="button"
                    tabIndex={-1}
                    onPointerDown={handlePointerDown}
                    onPointerUp={handlePlusPointerUp}
                  >
                    <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M7 1v12M1 7h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="bottom" align="start" onCloseAutoFocus={(e) => e.preventDefault()}>
                  <DropdownMenuItem onSelect={onAddTask}>
                    <Plus size={14} className="context-menu-content__item-icon" />
                    {t('project.addTask')}
                    <span className="context-menu-content__shortcut">{shortcutKey}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={onAddSection}>
                    <LayoutList size={14} className="context-menu-content__item-icon" />
                    {t('project.addSection')}
                    <span className="context-menu-content__shortcut">{sectionShortcut}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Arrow right */}
              <div className="add-task__arrow">
                <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4.5 0.5L8.5 6L4.5 11.5" stroke="currentColor" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
